import React, { useEffect, useMemo, useState } from 'react';
import { getApiClient } from '../../api/apiClient';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  ArrowPathIcon,
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

const Notifications = () => {
  const { t } = useLanguage();

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [page, setPage] = useState(1);
  const [size, setSize] = useState(25);
  const [search, setSearch] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  // Modal state
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Debounce search input
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(search.trim()), 400);
    return () => clearTimeout(id);
  }, [search]);

  const hasPrev = page > 1;
  const hasNext = useMemo(() => records.length === Number(size), [records, size]);

  const formatDateTime = (iso) => {
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return iso || '';
      return d.toLocaleString();
    } catch (e) {
      return iso || '';
    }
  };

  const fetchSms = async () => {
    try {
      setLoading(true);
      setError('');
      const params = new URLSearchParams();
      params.set('page', page.toString());
      params.set('size', size.toString());
      if (debouncedQuery) params.set('query', debouncedQuery);

      const client = getApiClient();
      const response = await client.get(`/admin/received/sms?${params.toString()}`);

      const data = Array.isArray(response.data) ? response.data : (response.data?.items || []);
      // Sort by colectedAt in descending order (latest first)
      const sortedData = data.sort((a, b) => {
        const dateA = new Date(a.colectedAt);
        const dateB = new Date(b.colectedAt);
        return dateB - dateA; // Descending order
      });
      setRecords(sortedData);
    } catch (err) {
      console.error('Failed to load received SMS:', err);
      setError(err?.message || 'Failed to load SMS');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, size, debouncedQuery]);

  // Modal handlers
  const openMessageModal = (message) => {
    setSelectedMessage(message);
    setIsModalOpen(true);
  };

  const closeMessageModal = () => {
    setIsModalOpen(false);
    setSelectedMessage(null);
  };

  return (
    <div className="mx-auto px-4 py-6 sm:px-6 md:px-8">
      {/* Controls */}
      <div className="bg-white shadow-lg rounded-t-xl p-6 border border-slate-200 border-b-0">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center space-x-4">
            <h3 className="text-lg font-semibold text-gray-900">{t('notifications')} – {t('receivedSms')}</h3>
            <span className="text-sm text-gray-500">{records.length} {t('entries')}</span>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={search}
                onChange={(e) => {
                  setPage(1);
                  setSearch(e.target.value);
                }}
                placeholder={t('search')}
                className="block w-64 pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>

            <select
              value={size}
              onChange={(e) => { setPage(1); setSize(Number(e.target.value)); }}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>

            <button
              onClick={fetchSms}
              className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-md text-sm font-medium hover:bg-purple-700 transition-colors"
            >
              <ArrowPathIcon className="w-4 h-4 mr-2" />
              {t('refresh')}
            </button>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 p-4 border-t-0">
          <div className="text-red-800 text-sm">{t('error')}: {error}</div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white shadow-lg rounded-b-xl border border-slate-200 border-t-0 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <ArrowPathIcon className="animate-spin h-10 w-10 text-purple-600" />
          </div>
        ) : records.length === 0 ? (
          <div className="text-center py-16 text-gray-500 text-sm">{t('noSms') || 'No SMS found'}</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('collectedAt') || 'Collected At'}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('phone')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('direction') || 'Direction'}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('smsMessage') || 'Message'}</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {records.map((r, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatDateTime(r.colectedAt)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{r.phoneNr}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          r.direction === 'INBOUND' ? 'bg-blue-100 text-blue-800' : 
                          r.direction === 'OUTBOUND' ? 'bg-green-100 text-green-800' : 
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {r.direction || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <div
                          className="max-w-2xl truncate cursor-pointer hover:text-purple-600 hover:underline"
                          title={t('clickToViewFullMessage') || 'Click to view full message'}
                          onClick={() => openMessageModal(r)}
                        >
                          {r.message}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-white">
              <div className="text-sm text-gray-600">
                {t('page') || 'Page'} {page}
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={!hasPrev}
                  className={`inline-flex items-center rounded-md border px-3 py-2 text-sm ${hasPrev ? 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300' : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'}`}
                >
                  <ChevronLeftIcon className="w-4 h-4 mr-1" />
                  {t('previous') || 'Previous'}
                </button>
                <button
                  onClick={() => setPage((p) => p + 1)}
                  disabled={!hasNext}
                  className={`inline-flex items-center rounded-md border px-3 py-2 text-sm ${hasNext ? 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300' : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'}`}
                >
                  {t('next') || 'Next'}
                  <ChevronRightIcon className="w-4 h-4 ml-1" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Message Modal */}
      {isModalOpen && selectedMessage && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-6 border w-11/12 md:w-3/4 lg:w-1/2 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {t('smsMessageDetails') || 'SMS Message Details'}
              </h3>
              <button
                onClick={closeMessageModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-600">{t('phone')}:</label>
                <p className="text-sm text-gray-900 font-mono">{selectedMessage.phoneNr}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-600">{t('collectedAt') || 'Collected At'}:</label>
                <p className="text-sm text-gray-900">{formatDateTime(selectedMessage.colectedAt)}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-600">{t('direction') || 'Direction'}:</label>
                <p className="text-sm text-gray-900">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    selectedMessage.direction === 'INBOUND' ? 'bg-blue-100 text-blue-800' : 
                    selectedMessage.direction === 'OUTBOUND' ? 'bg-green-100 text-green-800' : 
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {selectedMessage.direction || 'N/A'}
                  </span>
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-600">{t('smsMessage')}:</label>
                <div className="mt-1 p-3 bg-gray-50 rounded-md border">
                  <p className="text-sm text-gray-900 whitespace-pre-wrap break-words">
                    {selectedMessage.message}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-end mt-6 pt-4 border-t">
              <button
                onClick={closeMessageModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                {t('close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Notifications;


